import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  ArrowRight,
  CheckCircle2,
  Building2,
  Shield,
  Clock,
  AlertTriangle,
  Users,
  DollarSign,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const CustomerHACOV = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline:
      'Housing Authority of Covington cuts AP processing time 71% with EZ Cloud + Yardi Voyager',
    description:
      'HACOV cut invoice processing from 7 days to 2, reduced errors 80%, and saves ~$79K/year after implementing EZ Cloud with its Yardi Voyager integration in 2021.',
    image: 'https://ezcloud.ai/lovable-uploads/6b4db530-4be5-4352-b9a4-2deb72c24e98.png',
    author: { '@type': 'Organization', name: 'EZ Cloud' },
    publisher: {
      '@type': 'Organization',
      name: 'EZ Cloud',
      logo: {
        '@type': 'ImageObject',
        url: 'https://ezcloud.ai/lovable-uploads/6b4db530-4be5-4352-b9a4-2deb72c24e98.png',
      },
    },
    datePublished: '2024-01-01',
    mainEntityOfPage: 'https://ezcloud.ai/customers/hacov',
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="HACOV Case Study — Yardi AP Automation | EZ Cloud"
        description="How the Housing Authority of Covington cut AP processing time 71%, reduced errors 80%, and saves $79K annually with EZ Cloud's Yardi Voyager integration."
        canonical="https://ezcloud.ai/customers/hacov"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Customers', url: 'https://ezcloud.ai/customers' },
          { name: 'Housing Authority of Covington', url: 'https://ezcloud.ai/customers/hacov' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Building2 className="w-4 h-4" aria-hidden />
              Case Study · Property Management
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Housing Authority of Covington
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl">
              Seamless integration, unmatched efficiency: transforming HACOV's accounts payable with EZ Cloud's Yardi Voyager integration.
            </p>

            {/* Headline results */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              {[
                { value: '71%', label: 'Faster invoice processing', icon: Clock },
                { value: '80%', label: 'Reduction in errors', icon: AlertTriangle },
                { value: '$79K+', label: 'Saved annually', icon: DollarSign },
                { value: '3 → 25+', label: 'Active users', icon: Users },
              ].map(({ value, label, icon: Icon }) => (
                <div key={label} className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                  <Icon className="w-5 h-5 text-accent mb-2" aria-hidden />
                  <div className="text-3xl font-bold text-accent mb-1">{value}</div>
                  <div className="text-sm text-gray-600">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 max-w-4xl py-16 space-y-12">
          {/* At a glance */}
          <section aria-labelledby="glance">
            <h2 id="glance" className="text-2xl font-semibold text-gray-900 mb-4">
              At a glance
            </h2>
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div>
                  <dt className="text-gray-500">Industry</dt>
                  <dd className="text-gray-900 font-medium">Public housing / property management</dd>
                </div>
                <div>
                  <dt className="text-gray-500">ERP</dt>
                  <dd className="text-gray-900 font-medium">Yardi Voyager</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Implementation</dt>
                  <dd className="text-gray-900 font-medium">Live since 2021</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Integration</dt>
                  <dd className="text-gray-900 font-medium">EZ Cloud Nexus Integration Layer (bi-directional)</dd>
                </div>
              </dl>
            </Card>
          </section>

          {/* Challenges */}
          <section aria-labelledby="challenges">
            <h2 id="challenges" className="text-2xl font-semibold text-gray-900 mb-4">
              The challenge
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              HACOV's AP operations were slowed by manual processes that didn't fit a housing-authority workflow. Their team needed a system that integrated natively with Yardi, handled supplier-specific approval routing, and kept insurance certificates separate from invoices for construction contractors. Specifically:
            </p>
            <ul className="space-y-3">
              {[
                'Assigning specific user roles in the invoice approval process',
                'Automatically routing invoices to approvers based on supplier name',
                'Storing supplemental attachments — particularly construction insurance certificates — alongside invoices',
                'Differentiating invoices from insurance certificates during data entry',
                'Standardizing intercompany invoice processing across entities',
                'Capturing ACH payment data securely and reconciling back to Yardi',
              ].map((item) => (
                <li key={item} className="flex gap-3 text-gray-600">
                  <AlertTriangle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Solution */}
          <section aria-labelledby="solution">
            <h2 id="solution" className="text-2xl font-semibold text-gray-900 mb-4">
              The solution
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              EZ Cloud's Nexus Integration Layer connected directly to HACOV's Yardi Voyager, syncing invoice metadata, intercompany approvals, ACH payment data, and linked invoice images back to Yardi. The deployment addressed each challenge with a specific capability:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  title: 'Tailored invoice approval system',
                  body: 'Approvers assigned at data entry with email notifications for a streamlined approval flow.',
                },
                {
                  title: 'Auto-assignment by supplier',
                  body: 'Approvers auto-populated from the supplier name, with manual override when needed.',
                },
                {
                  title: 'Supplemental attachments at supplier level',
                  body: 'Construction insurance certificates stored alongside supplier records, easily accessible during review.',
                },
                {
                  title: 'Insurance-certificate identification',
                  body: 'Separate classification so certificates are never mis-filed as invoices.',
                },
                {
                  title: 'Intercompany standardization',
                  body: 'Single processing standard across HACOV entities, with real-time tracking between them.',
                },
                {
                  title: 'ACH payment capture → Yardi',
                  body: 'Payment data captured in EZ Cloud and transferred to Yardi without re-keying.',
                },
              ].map((item) => (
                <Card key={item.title} className="p-5 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Results */}
          <section aria-labelledby="results">
            <h2 id="results" className="text-2xl font-semibold text-gray-900 mb-6">
              The results
            </h2>
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">
                  Processing time: 7 days → 2 days (71% faster)
                </h3>
                <p>
                  Before EZ Cloud, HACOV's average invoice processing time was 7 days. With the automated workflows and Yardi integration in place, that dropped to 2 days — a 71% reduction that has sped up vendor payments and strengthened supplier relationships.
                </p>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">
                  Error rate down 80%
                </h3>
                <p>
                  Automated coding, approval routing, and the Yardi sync removed the manual re-keying that previously produced the bulk of errors. HACOV observed an 80% reduction in invoice-processing errors after rollout.
                </p>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">
                  Annual savings of approximately $79,166.67
                </h3>
                <p>
                  Reduced manual processing translated directly into labor savings. The captured savings are reinvested elsewhere in the organization.
                </p>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">
                  User adoption grew from 3 to 25+
                </h3>
                <p>
                  What started as a small pilot expanded organically — a signal of system usability and fit with how HACOV's teams already work.
                </p>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">SOC 2-compliant environment</h3>
                <p>
                  Throughout, EZ Cloud's SOC 2-compliant platform provided the security and data-handling standards a public-sector organization requires.
                </p>
              </div>
            </div>
          </section>

          {/* Quote */}
          <section aria-labelledby="quote">
            <h2 id="quote" className="sr-only">Customer quote</h2>
            <Card className="p-8 bg-accent/10 border-accent/30">
              <blockquote className="text-lg md:text-xl text-gray-900 leading-relaxed italic mb-4">
                "EZ Cloud has made my life in a busy accounts payable environment more efficient. The whole team was skeptical at first about how EZ Cloud and Yardi were going to work together, but they have — and it has been seamless. EZ Cloud has increased the productivity of processing invoices, and the team loves not having to physically sign approval on each invoice. Now they receive a notification, click the link, review, and approve. It's easy."
              </blockquote>
              <footer className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-accent" aria-hidden />
                <div>
                  <div className="text-gray-900 font-semibold">Denise Steffen</div>
                  <div className="text-sm text-gray-600">Staff Accountant · Housing Authority of Covington</div>
                </div>
              </footer>
            </Card>
          </section>

          {/* Related */}
          <section aria-labelledby="related" className="border-t border-gray-200 pt-10">
            <h2 id="related" className="text-2xl font-semibold text-gray-900 mb-6">
              Related EZ Cloud resources
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/integrations/yardi"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Yardi Voyager integration
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  The native Yardi integration that powered HACOV's deployment — bi-directional sync, intercompany flows, ACH capture.
                </p>
              </Link>

              <Link
                to="/industries/property-management"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Property management AP
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Multi-property GL coding, approval routing by property, month-end utility throughput — the full property-management AP solution.
                </p>
              </Link>

              <Link
                to="/customers"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  More customer stories
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real outcomes from EZ Cloud customers across property management, healthcare, manufacturing, and more.
                </p>
              </Link>

              <Link
                to="/resources/roi-calculator"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Model your savings
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Plug your numbers into the EZ Cloud ROI calculator and see annual savings for your AP team.
                </p>
              </Link>

              <Link
                to="/technology/native-erp"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  How native ERP integration works
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real-time APIs, zero-downtime deployment, SOC 2 compliance — the architecture behind HACOV's deployment.
                </p>
              </Link>

              <Link
                to="/integrations"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  See all ERP integrations
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Yardi is one of five prebuilt EZ Cloud ERP integrations — Oracle, NetSuite, SAP, Dynamics too.
                </p>
              </Link>
            </div>
          </section>

          {/* CTA */}
          <section className="border-t border-gray-200 pt-12 text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              See what EZ Cloud can do for your AP team
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              If your team runs Yardi, Oracle, SAP, NetSuite, or Dynamics — we integrate natively. Book a 20-minute demo and we'll walk through your specific workflow.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                onClick={() =>
                  openCalendlyDemo()
                }
              >
                Book a Yardi AP demo
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a
                  href="/case-studies/EZ-Cloud-HACOV-Case-Study.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </Button>
              <Button size="lg" variant="ghost" asChild>
                <Link to="/industries/property-management">
                  See our property management solution
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default CustomerHACOV;
